// src/Utils/urineColors.js
// Urine Color is observed by the technician, not read off the strip, so it is
// picked from a swatch dropdown instead of being typed/fetched.
// `value` is what the result API receives; `label` and `hex` are display only.
// `isNormal` marks the safe range: colours that only reflect how hydrated the
// patient is. Everything else warrants a second look.

export const URINE_COLOR_PARAMETER_ID = "KIU7412";

export const URINE_COLOR_OPTIONS = [
  { value: "COLORLESS", label: "Colorless", hex: "#F8FBFF", isNormal: true },
  {
    value: "PALE_YELLOW",
    label: "Pale Yellow (Straw)",
    hex: "#FFF2A6",
    isNormal: true,
  },
  { value: "YELLOW", label: "Yellow", hex: "#FFD84A", isNormal: true },
  { value: "DARK_YELLOW", label: "Dark Yellow", hex: "#D9AA00", isNormal: true },
  { value: "AMBER", label: "Amber", hex: "#B86F00", isNormal: true },
  { value: "ORANGE", label: "Orange", hex: "#DC6B1C" },
  { value: "PINK", label: "Pink", hex: "#E79AA6" },
  { value: "RED", label: "Red", hex: "#A63A37" },
  { value: "BROWN_TEA", label: "Brown / Tea-colored", hex: "#6B4423" },
  { value: "GREEN", label: "Green", hex: "#5F8150" },
  { value: "BLUE_GREEN", label: "Blue / Blue-green", hex: "#3F7F86" },
  { value: "PURPLE", label: "Purple", hex: "#71517E" },
  { value: "WHITE_MILKY", label: "White / Milky", hex: "#EEEBDD" },
  { value: "BLACK", label: "Black", hex: "#211F1B" },
];

const URINE_COLOR_BY_VALUE = URINE_COLOR_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option;
  return acc;
}, {});

// The safe range, derived from the options so the two can never drift apart.
export const URINE_COLOR_NORMAL_OPTIONS = URINE_COLOR_OPTIONS.filter(
  (option) => option.isNormal
);

export const URINE_COLOR_NORMAL_VALUES = new Set(
  URINE_COLOR_NORMAL_OPTIONS.map((option) => option.value)
);

// Shown in the reference-range column, where the other rows print "unit range".
export const URINE_COLOR_REFERENCE_RANGE = "Colorless - Amber";

export const URINE_COLOR_REFERENCE_NOTE =
  "Colorless, Pale Yellow (Straw), Yellow, Dark Yellow and Amber are normal hydration variations.";

export const getUrineColorOption = (value) => {
  if (typeof value !== "string") return null;
  const key = value.trim().toUpperCase();
  return URINE_COLOR_BY_VALUE[key] || null;
};

export const getUrineColorLabel = (value) =>
  getUrineColorOption(value)?.label || "";

// True only for a recognised colour inside the safe range. An unset or
// unrecognised value is not "abnormal", so callers check hasValue separately.
export const isNormalUrineColor = (value) =>
  getUrineColorOption(value)?.isNormal === true;

// A colour that was picked, is recognised, and sits outside the safe range.
export const isAbnormalUrineColor = (value) => {
  const option = getUrineColorOption(value);
  return option ? option.isNormal !== true : false;
};

// Match on ParameterID first, falling back to the name so the picker still
// renders if the panel is served with a different id.
export const isUrineColorParam = (detail) => {
  const parameterId = (detail?.ParameterID || "").trim().toUpperCase();
  if (parameterId === URINE_COLOR_PARAMETER_ID) return true;

  const name = (detail?.name || "").trim().toLowerCase();
  return name === "urine color" || name === "urine colour";
};
